import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ApiProvider } from './contexts/ApiContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import VerifyEmailSent from './pages/VerifyEmailSent';
import EmailVerification from './pages/EmailVerification';
import ResendVerification from './pages/ResendVerification';
import AcceptInvitation from './pages/AcceptInvitation';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import PageLoader from './components/PageLoader';
import ErrorFallback from './components/ErrorFallback';
import { ErrorBoundary } from 'react-error-boundary';
import { ToastContainer } from 'react-toastify';
import { Toaster } from 'react-hot-toast';
import 'react-toastify/dist/ReactToastify.css';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Documentation = lazy(() => import('./pages/Documentation'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const UserProfile = lazy(() => import('./pages/settings/UserProfile'));
const OrganizationUnits = lazy(() => import('./pages/otms/OrganizationUnits'));
const SOPManagement = lazy(() => import('./pages/otms/SOPManagement'));
const PersonsManagement = lazy(() => import('./pages/otms/PersonsManagement'));
const WorkProgress = lazy(() => import('./pages/otms/WorkProgress'));
const TemplateGallery = lazy(() => import('./pages/otms/TemplateGallery'));
const ChatManagement = lazy(() => import('./pages/otms/ChatManagement'));
const EngagePage = lazy(() => import('./components/otms/engage/EngagePage'));
const EngageNotifications = lazy(() => import('./components/otms/engage/EngageNotifications'));
const Reports = lazy(() => import('./pages/Reports'));
const Search = lazy(() => import('./pages/Search'));
const ShareFeedback = lazy(() => import('./pages/ShareFeedback'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ConnectAi = lazy(() => import('./pages/ConnectAi'));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader label="Checking your session…" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: window.location.pathname }} replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader label="Loading…" />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Router>
        <ApiProvider>
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route path="/home" element={<Navigate to="/dashboard" replace />} />
                <Route path="/documentation" element={<Documentation />} />
                <Route path="/faq" element={<FAQ />} />
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route path="/onboarding/:productId" element={<Onboarding />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/verify-email-sent" element={<VerifyEmailSent />} />
                <Route path="/verify-email" element={<EmailVerification />} />
                <Route path="/resend-verification" element={<ResendVerification />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/search"
                  element={
                    <ProtectedRoute>
                      <Search />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/privacy-policy"
                  element={
                    <ProtectedRoute>
                      <PrivacyPolicy />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/share-feedback"
                  element={
                    <ProtectedRoute>
                      <ShareFeedback />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/connect-ai"
                  element={
                    <ProtectedRoute>
                      <ConnectAi />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/*"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/otms/organization-units"
                  element={
                    <ProtectedRoute>
                      <OrganizationUnits />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/otms/sop"
                  element={
                    <ProtectedRoute>
                      <SOPManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/otms/personnel"
                  element={
                    <ProtectedRoute>
                      <PersonsManagement />
                    </ProtectedRoute>
                  }
                />
                <Route path="/talk" element={<Navigate to="/chat" replace />} />
                <Route
                  path="/work"
                  element={
                    <ProtectedRoute>
                      <WorkProgress />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates"
                  element={
                    <ProtectedRoute>
                      <TemplateGallery />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/chat"
                  element={
                    <ProtectedRoute>
                      <ChatManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/engage"
                  element={
                    <ProtectedRoute>
                      <EngagePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/engage/groups"
                  element={
                    <ProtectedRoute>
                      <EngagePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/engage/groups/:groupId"
                  element={
                    <ProtectedRoute>
                      <EngagePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/engage/my-posts"
                  element={
                    <ProtectedRoute>
                      <EngagePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/engage/profile"
                  element={
                    <ProtectedRoute>
                      <EngagePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/engage/profile/:userId"
                  element={
                    <ProtectedRoute>
                      <EngagePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/engage/notifications"
                  element={
                    <ProtectedRoute>
                      <EngageNotifications />
                    </ProtectedRoute>
                  }
                />

                {process.env.NODE_ENV === 'development' && (
                  <Route path="/test/accept-invitation" element={<AcceptInvitation />} />
                )}
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/test/reset-password" element={<ResetPassword />} />
                )}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <ToastContainer position="top-right" />
            <Toaster position="top-right" />
          </AuthProvider>
        </ApiProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
